import { IconError, IconOutlineSuccess } from "assets/img/icons";
import { PLACEHOLDER_IN_BASE64 } from "constants/common";
import THEME from "constants/themes";
import { SOCIAL_NETWORK_LIST } from "constants/widgetBuilder";
import { UploadedContentTypes, WidgetDetailTypes } from "models/Widget";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUrlS3 } from "services/funcHelper";
import utils from "services/utils";
import {
	StyledFlex,
	StyledIcon,
	StyledImg,
	StyledText,
	StyledWrapper,
} from "styled-components/Common/CommonStyled";
import { StyledWidgetSectionWrapper } from "styled-components/DynamicWidget/DynamicWidgetStyled";
import {
	StyledLink,
	StyledMessage,
	StyledSubmitButton,
	StyledTooltip,
} from "styled-components/WidgetBuilder/WidgetBuilderStyled";

type WidgetUploadConfirmationProps = {
	widgetDetails: WidgetDetailTypes;
	handleUploadMore: () => void;
};

const WidgetUploadConfirmation = (props: WidgetUploadConfirmationProps) => {
	const { t } = useTranslation();
	const { widgetDetails, handleUploadMore } = props;
	const isMobile = utils.isMobileWidth();
	const { metadata } = widgetDetails;
	const { colors, body } = metadata || {};
	const { advancedSettings } = body || {};
	const {
		confirmationMessage = "",
		confirmationButtonText = "",
		visibleSocialButtons = false,
		socialMessage = "",
	} = advancedSettings || {};
	const { bgSubmit = "", buttonUrl, highlight } = colors || {};

	const { uploadedContentListResult } = useSelector((state: any) => state.widgets);

	const renderFileItem = (item: UploadedContentTypes) => {
		return (
			<StyledWrapper
				key={item.requestId}
				margin="0 0 12px 0"
				padding="8px 16px"
				borderRadius="12px"
				background="rgba(224, 227, 229, 0.20)"
				display="flex"
				justifyContent="space-between"
				alignItems="center">
				<StyledWrapper maxWidth="calc(100% - 24px)" display="flex" gap="8px" alignItems="center">
					<StyledWrapper
						minWidth="32px"
						width="32px"
						height="32px"
						borderRadius="8px"
						overflow="hidden">
						<StyledImg
							src={PLACEHOLDER_IN_BASE64.IMAGE}
							width={"100%"}
							height={"100%"}
							preview={false}
							objectFit="cover"
						/>
					</StyledWrapper>
					<StyledText>{item?.file?.name || ""}</StyledText>
				</StyledWrapper>

				{!item?.limitationError ? (
					<StyledIcon size={20}>
						<IconOutlineSuccess />
					</StyledIcon>
				) : (
					<StyledTooltip
						overlayClassName="upload-status-error-tooltip"
						title={
							<StyledWrapper
								dangerouslySetInnerHTML={{
									__html: item?.limitationError?.msg || "",
								}}
							/>
						}
						trigger={["hover", "focus"]}>
						<StyledIcon padding="0 4px" cursor="pointer" colorPath={THEME.colors.redBase} size={18}>
							<IconError />
						</StyledIcon>
					</StyledTooltip>
				)}
			</StyledWrapper>
		);
	};

	const renderFileList = () => {
		return uploadedContentListResult.length ? (
			<StyledWidgetSectionWrapper borderRadius="8px">
				{uploadedContentListResult.map((item: UploadedContentTypes) => renderFileItem(item))}
			</StyledWidgetSectionWrapper>
		) : (
			<></>
		);
	};

	return (
		<StyledWrapper textAlign="center">
			<StyledIcon>
				<IconOutlineSuccess />
			</StyledIcon>
			<StyledText
				margin="8px 0"
				fontSize="24px"
				fontWeight={700}
				color={THEME.colors.black}
				lineHeight={1.25}>
				{t("message.upload_successful")}
			</StyledText>
			<StyledText fontSize="16px" color={THEME.colors.black} lineHeight={1.25}>
				{confirmationMessage || t("message.uploaded_successfully")}
			</StyledText>

			{renderFileList()}

			<StyledSubmitButton
				height="36px"
				width="unset"
				minWidth="50%"
				maxWidth="100%"
				className={buttonUrl ? "has_url" : ""}
				backgroundUrl={getUrlS3(buttonUrl || "")}
				onClick={handleUploadMore}
				backgroundColor={bgSubmit}
				highlight={highlight}
				borderRadius="none"
				margin="24px 0 0 0">
				<StyledText fontSize="16px">{confirmationButtonText || t("button.upload_more")}</StyledText>
			</StyledSubmitButton>

			{visibleSocialButtons && (
				<StyledWrapper margin="16px 0">
					<StyledMessage fontWeight="400">{socialMessage}</StyledMessage>
					<StyledFlex justify="center" margin="16px 0 ">
						{SOCIAL_NETWORK_LIST.map((item, index) => {
							return (
								<StyledLink key={index} href={item.href} target="_blank">
									<StyledIcon width="34px" height="34px" margin={isMobile ? "0 4px" : "0 8px"}>
										{item.icon}
									</StyledIcon>
								</StyledLink>
							);
						})}
					</StyledFlex>
				</StyledWrapper>
			)}
		</StyledWrapper>
	);
};

export default WidgetUploadConfirmation;
